import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Syringe, Stethoscope, Check, Trash2, Bell, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner, EmptyState } from "@/components/kit";
import { formatDate } from "@/lib/files";
import DogAvatar from "@/components/DogAvatar";

export default function Reminders() {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        apiFetch("/reminders"),
        apiFetch("/dogs").catch(() => []),
      ]);
      setReminders(r || []);
      setDogs(d || []);
      setLocked(false);
    } catch (err) {
      if (err.status === 402) setLocked(true);
      else toast.error(err.message || "Could not load reminders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const complete = async (id) => {
    try {
      await apiFetch(`/reminders/${id}/complete`, { method: "POST" });
      toast.success("Marked done");
      load();
    } catch (err) {
      toast.error(err.message || "Failed");
    }
  };

  const del = async (id) => {
    try {
      await apiFetch(`/reminders/${id}`, { method: "DELETE" });
      load();
    } catch (err) {
      toast.error(err.message || "Failed");
    }
  };

  if (loading) return <FullPageSpinner />;

  if (locked) {
    return (
      <div className="max-w-lg mx-auto">
        <PageHeader title="Reminders" backTo="/app" />
        <div className="rounded-3xl bg-[#F6DED5] p-8 text-center">
          <div className="h-16 w-16 rounded-3xl bg-white flex items-center justify-center mx-auto mb-4">
            <Sparkles size={30} color="#E86A45" />
          </div>
          <h3 className="font-display text-2xl font-extrabold">A Wooflect Pro feature</h3>
          <p className="text-[#4A4746] mt-2">Get vaccine and vet-visit reminders (with email alerts 7 & 3 days before) — never miss a shot again.</p>
          <button
            data-testid="reminders-upgrade-btn"
            onClick={() => navigate("/app/subscription?highlight=pawpal_pro")}
            className="mt-5 rounded-full bg-[#E86A45] text-white font-extrabold px-8 py-3 hover:bg-[#d65f3c]"
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="Reminders"
        subtitle="Vaccines & vet visits for your dogs."
        backTo="/app"
        right={
          <button
            data-testid="reminders-add-btn"
            onClick={() => navigate("/app/reminders/new")}
            className="flex items-center gap-1.5 rounded-full bg-[#E86A45] text-white font-extrabold px-4 py-2.5"
          >
            <Plus size={17} /> Add
          </button>
        }
      />

      {reminders.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="No reminders yet"
          body="Add a vaccine or vet-visit reminder to stay on track."
          action={
            <button onClick={() => navigate("/app/reminders/new")} className="rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-3">
              Add reminder
            </button>
          }
        />
      ) : (
        <div className="space-y-2">
          {reminders.map((r) => (
            <div
              key={r.id}
              data-testid={`reminder-${r.id}`}
              className={`flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8E3DA] ${r.completed ? "opacity-60" : ""}`}
            >
              <div className={`relative h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${r.type === "vaccine" ? "bg-[#E8F0E5]" : r.type === "care" ? "bg-[#FBF0DC]" : "bg-[#F6DED5]"}`}>
                {r.type === "vaccine" ? <Syringe size={18} color="#6F8F72" /> : r.type === "care" ? <Lightbulb size={18} color="#E8A33D" /> : <Stethoscope size={18} color="#E86A45" />}
                {(() => {
                  const dog = dogs.find((d) => d.id === r.dog_id);
                  return dog ? <DogAvatar dog={dog} size={20} className="absolute -bottom-1 -right-1 ring-2 ring-white" /> : null;
                })()}
              </div>
              <button className="flex-1 min-w-0 text-left" onClick={() => navigate(`/app/reminders/${r.id}`)}>
                <p className="font-bold truncate">{r.title}</p>
                <p className="text-sm text-[#4A4746]">
                  Due {formatDate(r.due_date)}{r.recurrence === "yearly" ? " · yearly" : ""}
                </p>
              </button>
              {!r.completed && (
                <button data-testid={`reminder-complete-${r.id}`} onClick={() => complete(r.id)} className="h-9 w-9 rounded-full bg-[#E8F0E5] flex items-center justify-center" title="Mark done">
                  <Check size={17} color="#6F8F72" />
                </button>
              )}
              <button data-testid={`reminder-delete-${r.id}`} onClick={() => del(r.id)} className="h-9 w-9 rounded-full bg-[#FDECEB] flex items-center justify-center" title="Delete">
                <Trash2 size={16} color="#D9534F" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
